import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { motion, useInView, AnimatePresence } from "framer-motion";
import { useRef, useState } from "react";
import { ArrowUpRight, X } from "lucide-react";

const companies = [
  {
    name: "EcoFeed",
    tagline: "Advanced aquaculture feeding systems",
    description:
      "EcoFeed is a South Korean aquaculture technology company developing advanced fish feeding systems designed to optimize feed efficiency and reduce operational costs for fish farms. The company's proprietary technology enables more precise feeding processes, improving productivity and sustainability in aquaculture operations. EcoFeed focuses on modernizing fish farming infrastructure and expanding its solutions to international markets, including North America.",
    rationale:
      "EcoFeed addresses a critical efficiency gap in global aquaculture, a sector with strong secular growth. The company's precision feeding technology reduces waste and operating costs, creating measurable ROI for farm operators. Its international expansion strategy aligns with Nebula Flow's cross-border investment thesis.",
    location: "Seoul, South Korea",
    initial: "Ec",
  },
  {
    name: "ProMap",
    tagline: "Digital marketplace for repair specialists",
    description:
      "ProMap is a digital platform that connects consumers with qualified repair specialists through a mobile application. The service simplifies the process of finding reliable professionals for home and device repairs while maintaining transparent and affordable pricing. By digitizing the repair marketplace, ProMap aims to improve accessibility and efficiency in local service networks.",
    rationale:
      "ProMap captures a fragmented and underserved market in local repair services. The platform's mobile-first approach and transparent pricing model drive strong consumer retention and repeat usage, supporting a scalable marketplace model with favorable unit economics.",
    location: "Toronto, Canada",
    initial: "Pr",
  },
  {
    name: "uNexus",
    tagline: "Personalized learning platform",
    description:
      "uNexus is an education technology company developing a digital platform that personalizes learning resources based on the needs and progress of individual students. The platform uses data-driven tools to help educators and learners navigate diverse educational pathways and improve learning outcomes. uNexus focuses on modernizing educational delivery and supporting the evolving digital education ecosystem.",
    rationale:
      "uNexus leverages data-driven personalization to address systemic inefficiencies in educational delivery. The platform's adaptive approach creates strong engagement metrics and positions the company at the intersection of EdTech and data infrastructure, two sectors with sustained institutional demand.",
    location: "Vancouver, Canada",
    initial: "uN",
  },
  {
    name: "Numano",
    tagline: "Physical-to-digital retail transition platform",
    description:
      "Numano is a technology company focused on enhancing the online retail experience by enabling businesses to transition from traditional physical stores to modern digital commerce platforms. Its solutions are designed to integrate retail operations with seamless online purchasing experiences. Numano supports retailers seeking to expand their presence beyond domestic markets and operate in global e-commerce environments.",
    rationale:
      "Numano operates in the growing digital transformation space for retail, where legacy businesses require modern tooling to compete. The company's integrated approach to omnichannel commerce creates strong retention among merchants and supports a land-and-expand revenue model.",
    location: "Toronto, Canada",
    initial: "Nu",
  },
  {
    name: "Athlete Pro Junior",
    tagline: "Athlete training and career management",
    description:
      "Athlete Pro is a digital platform designed to support athletes by providing tools for training management, performance tracking, and professional development. The application connects athletes, coaches, and organizations in a single ecosystem that helps streamline communication and performance analysis. Athlete Pro aims to improve how athletes manage their careers and training environments.",
    rationale:
      "Athlete Pro addresses a structurally underserved market in athlete management. By consolidating training, performance, and career tools into a unified platform, the company creates high switching costs and deep engagement across its user base, supporting a durable SaaS model.",
    location: "Montreal, Canada",
    initial: "Ap",
  },
  {
    name: "SightView",
    tagline: "Intelligent retail video analytics",
    description:
      "SightView provides cloud-based surveillance and analytics solutions designed for retail environments. The platform enables businesses to monitor customer movement and interaction with products in stores using intelligent video analytics. By transforming surveillance data into actionable insights, SightView helps retailers optimize store layouts, product placement, and marketing strategies.",
    rationale:
      "SightView transforms existing retail surveillance infrastructure into a data asset. The company's analytics layer creates recurring revenue from actionable intelligence, operating in a market with strong demand for data-driven physical retail optimization.",
    location: "Toronto, Canada",
    initial: "Sv",
  },
  {
    name: "Lambormat",
    tagline: "Non-invasive jewelry analysis technology",
    description:
      "Lambormat develops advanced technologies for the non-invasive evaluation of jewelry and precious materials. Its proprietary device uses laser spectrometry to analyze jewelry composition without causing damage to the item being tested. The technology enables more accurate and efficient assessments for jewelry professionals and appraisal services.",
    rationale:
      "Lambormat's proprietary laser spectrometry technology creates a defensible position in a niche but high-value market. The non-invasive approach solves a critical pain point for jewelry professionals, and the company's hardware-plus-software model supports strong margins and recurring service revenue.",
    location: "Tel Aviv, Israel",
    initial: "Lb",
  },
  {
    name: "DMaple",
    tagline: "Blockchain-powered cross-border payments",
    description:
      "DMaple is a fintech platform designed to simplify cross-border payments using blockchain-based infrastructure. The system enables users to send and receive digital payments through a streamlined interface that reduces complexity often associated with cryptocurrency transactions. DMaple focuses on making international transfers more accessible for individuals and businesses operating globally.",
    rationale:
      "DMaple simplifies a complex cross-border payments process using blockchain infrastructure. The platform's focus on accessibility and reduced transaction friction positions it to capture share in a rapidly expanding market for international digital payments.",
    location: "Toronto, Canada",
    initial: "Dm",
  },
];

/* ─── Animated wrapper ─── */
const Reveal = ({ children, delay = 0, className = "" }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-50px" });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 22 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.55, delay, ease: [0.22, 1, 0.36, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

export default function Portfolio() {
  const [selectedCompany, setSelectedCompany] = useState(null);
  const heroRef = useRef(null);
  const heroInView = useInView(heroRef, { once: true, margin: "-40px" });

  return (
    <div className="min-h-screen bg-white" data-testid="portfolio-page">
      <Navbar />

      {/* ═══════════════════════════════════════════════ */}
      {/* HERO + CARDS — continuous background              */}
      {/* ═══════════════════════════════════════════════ */}
      <section
        ref={heroRef}
        className="relative overflow-hidden pt-32 md:pt-40 pb-[54px] md:pb-10"
        data-testid="portfolio-hero"
        style={{ background: 'linear-gradient(180deg, #f8faff 0%, #f3f5fb 50%, #f8faff 100%)' }}
      >
        {/* Grid pattern — matches About page */}
        <div className="absolute inset-0 grid-pattern opacity-[0.06]" />

        {/* Left radial glow */}
        <motion.div
          className="absolute pointer-events-none"
          style={{ top: '5%', left: '-8%' }}
          animate={{ x: [0, 10, 0], y: [0, -6, 0] }}
          transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
        >
          <div className="w-[500px] h-[600px] md:w-[600px] md:h-[700px] rounded-full blur-[140px]"
            style={{ background: 'radial-gradient(circle, rgba(79,107,255,0.12) 0%, rgba(79,107,255,0.04) 50%, transparent 75%)' }}
          />
        </motion.div>

        {/* Right radial glow */}
        <motion.div
          className="absolute pointer-events-none"
          style={{ bottom: '-10%', right: '-6%' }}
          animate={{ x: [0, -8, 0], y: [0, 10, 0] }}
          transition={{ duration: 14, repeat: Infinity, ease: "easeInOut" }}
        >
          <div className="w-[400px] h-[500px] md:w-[500px] md:h-[600px] rounded-full blur-[140px]"
            style={{ background: 'radial-gradient(circle, rgba(123,97,255,0.10) 0%, rgba(79,107,255,0.04) 50%, transparent 75%)' }}
          />
        </motion.div>

        {/* Smooth fade to white at the bottom */}
        <div className="absolute bottom-0 left-0 right-0 h-32 pointer-events-none" style={{ background: 'linear-gradient(to bottom, transparent, #ffffff)' }} />

        {/* Hero content */}
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 text-center">
          {/* Overline */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={heroInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.05 }}
            className="flex items-center justify-center gap-3 mb-8"
          >
            <div className="w-8 h-px bg-[#4F6BFF]" />
            <span className="font-body text-xs font-semibold uppercase tracking-[0.18em] text-[#4F6BFF]/80">
              Our Companies
            </span>
            <div className="w-8 h-px bg-[#4F6BFF]" />
          </motion.div>

          {/* Title */}
          <motion.h1
            initial={{ opacity: 0, y: 18 }}
            animate={heroInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.65, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
            className="font-body text-[30px] sm:text-[45px] tracking-tight leading-tight font-semibold text-[#08122a] mb-6"
            data-testid="portfolio-heading"
          >
            Portfolio
          </motion.h1>

          {/* Description */}
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={heroInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.22 }}
            className="font-body text-[15px] md:text-base leading-[1.75] text-[#475569] max-w-[692px] mx-auto"
            data-testid="portfolio-intro"
          >
            Nebula Flow partners with early-stage companies building
            foundational technology across regulated markets, infrastructure,
            and digital commerce. Our portfolio reflects a concentrated,
            thesis-driven approach to value creation.
          </motion.p>
        </div>

        {/* Cards grid */}
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 mt-12 md:mt-16">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="portfolio-cards">
            {companies.map((company, i) => (
              <Reveal key={company.name} delay={i * 0.05}>
                <button
                  onClick={() => setSelectedCompany(company)}
                  data-testid={`company-card-${company.name.toLowerCase().replace(/\s+/g, "-")}`}
                  className="w-full text-left group"
                >
                  <div className="relative rounded-2xl border border-black/[0.04] p-7 md:p-8 transition-all duration-300 hover:-translate-y-[3px] cursor-pointer bg-white overflow-hidden portfolio-card card-gradient-line">

                    {/* Large monogram watermark */}
                    <div className="absolute -right-3 -bottom-5 font-heading text-[7.5rem] font-bold leading-none text-slate-50 select-none pointer-events-none transition-colors duration-300 group-hover:text-blue-50/80">
                      {company.initial}
                    </div>

                    {/* Content */}
                    <div className="relative z-10">
                      {/* Row: Name + Arrow */}
                      <div className="flex items-start justify-between gap-4 mb-1.5">
                        <h3 className="font-body text-[20px] md:text-[22px] font-semibold text-[#08122a] tracking-tight transition-colors duration-200 group-hover:text-[#4F6BFF]">
                          {company.name}
                        </h3>
                        <div className="flex-shrink-0 w-8 h-8 rounded-full border border-slate-150 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300 translate-x-1 group-hover:translate-x-0 bg-white">
                          <ArrowUpRight className="w-3.5 h-3.5 text-[#4F6BFF]" />
                        </div>
                      </div>

                      {/* Location */}
                      <p className="font-body text-[13px] text-[#94a3b8] mb-4">
                        {company.location}
                      </p>

                      {/* Tagline */}
                      <p className="font-body text-[15px] text-[#475569] leading-relaxed">
                        {company.tagline}
                      </p>
                    </div>
                  </div>
                </button>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
      {/* ═══════════════════════════════════════════════ */}
      <AnimatePresence>
        {selectedCompany && (
          <motion.div
            key="modal-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 md:p-6"
            data-testid="company-modal-backdrop"
            onClick={() => setSelectedCompany(null)}
          >
            {/* Blurred backdrop */}
            <div className="absolute inset-0 bg-[#0F172A]/35 backdrop-blur-md" />

            {/* Modal panel */}
            <motion.div
              key="modal-content"
              initial={{ opacity: 0, y: 28, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 18, scale: 0.97 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              className="relative z-10 bg-white rounded-3xl w-full max-w-[640px] max-h-[88vh] overflow-y-auto"
              style={{
                boxShadow:
                  "0 32px 80px rgba(15,23,42,0.18), 0 12px 32px rgba(15,23,42,0.06)",
              }}
              onClick={(e) => e.stopPropagation()}
              data-testid="company-modal"
            >
              {/* Top accent bar */}
              <div className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-[#4F6BFF] via-[#6356F0] to-[#7B61FF] rounded-t-3xl" />

              {/* Close */}
              <button
                onClick={() => setSelectedCompany(null)}
                className="absolute top-5 right-5 w-9 h-9 rounded-full bg-slate-100/80 hover:bg-slate-200 flex items-center justify-center transition-colors duration-200 z-20"
                data-testid="modal-close-btn"
              >
                <X className="w-4 h-4 text-slate-500" />
              </button>

              {/* Header */}
              <div className="px-8 pt-9 pb-6">
                <h2 className="font-body text-[24px] font-semibold text-[#08122a] tracking-tight mb-1">
                  {selectedCompany.name}
                </h2>
                <p className="font-body text-[13px] text-[#94a3b8]">
                  {selectedCompany.location}
                </p>
              </div>

              {/* Divider */}
              <div className="mx-8 h-px bg-slate-100" />

              {/* Body */}
              <div className="px-8 py-7 space-y-7">
                <div>
                  <p className="font-body text-[11px] font-semibold uppercase tracking-[0.16em] text-[#94a3b8] mb-3">
                    Overview
                  </p>
                  <p className="font-body text-[15px] leading-[1.8] text-[#334155]">
                    {selectedCompany.description}
                  </p>
                </div>

                <div>
                  <p className="font-body text-[11px] font-semibold uppercase tracking-[0.16em] text-[#94a3b8] mb-3">
                    Investment Rationale
                  </p>
                  <p className="font-body text-[15px] leading-[1.8] text-[#334155]">
                    {selectedCompany.rationale}
                  </p>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <Footer />
    </div>
  );
}
